/**
 * Formats a date string to a more readable format
 * @param dateString - ISO date string
 * @param locale - Locale for formatting (default: 'en-IN')
 * @returns Formatted date string
 */
export const formatDate = (dateString: string, locale: string = 'en-IN'): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Converts an NSE index name (e.g. "Nifty 50", "Nifty Bank") into the same
 * filesystem/blob-safe file name the calculator uses to store its history file
 * (e.g. "NIFTY_50", "NIFTY_BANK"). Mirrors IndexHistoryService.ToFileSafeName in the backend.
 */
export const toIndexFileSafeName = (indexName: string): string =>
  indexName
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .toUpperCase();

/**
 * Sorts analytics data by date
 * @param data - Array of analytics data
 * @param order - Sort order ('asc' or 'desc')
 * @returns Sorted array
 */
export const sortByDate = <T extends { Date: string }>(data: T[], order: 'asc' | 'desc' = 'desc'): T[] => {
  return [...data].sort((a, b) => {
    const dateA = new Date(a.Date).getTime();
    const dateB = new Date(b.Date).getTime();
    return order === 'desc' ? dateB - dateA : dateA - dateB;
  });
};

/**
 * Calculate percentage with safe division
 * @param numerator - Top number
 * @param denominator - Bottom number
 * @param decimals - Number of decimal places (default: 2)
 * @returns Percentage as number
 */
export const calculatePercentage = (
  numerator: number,
  denominator: number,
  decimals: number = 2
): number => {
  if (denominator === 0) return 0;
  return Number(((numerator / denominator) * 100).toFixed(decimals));
};

/**
 * Calculate ratio percentage safely
 * @param above - Count above threshold
 * @param below - Count below threshold
 * @param decimals - Number of decimal places (default: 2)
 * @returns Percentage as number
 */
export const calculateRatioPercentage = (
  above: number,
  below: number,
  decimals: number = 2
): number => {
  const total = above + below;
  return calculatePercentage(above, total, decimals);
};

/**
 * Filter data by date range
 * @param data - Array of data with Date property
 * @param fromDate - Start date (ISO string)
 * @param toDate - End date (ISO string)
 * @returns Filtered array
 */
export const filterByDateRange = <T extends { Date: string }>(
  data: T[],
  fromDate: string,
  toDate: string
): T[] => {
  return data.filter((item) => item.Date >= fromDate && item.Date <= toDate);
};

/**
 * Finds the index of the entry whose `Date` is closest to the given ISO date string.
 * Returns -1 when the array is empty or the date is falsy.
 */
export const findNearestDateIndex = <T extends { Date: string }>(data: T[], isoDate: string): number => {
  if (data.length === 0 || !isoDate) {
    return -1;
  }

  const target = new Date(isoDate).getTime();
  let closestIndex = 0;
  let closestDiff = Infinity;

  data.forEach((entry, index) => {
    const diff = Math.abs(new Date(entry.Date).getTime() - target);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestIndex = index;
    }
  });

  return closestIndex;
};

/**
 * Get date range from days back
 * @param daysBack - Number of days to go back
 * @param referenceDate - Reference date (default: today)
 * @returns Object with fromDate and toDate as ISO strings
 */
export const getDateRange = (daysBack: number, referenceDate: Date = new Date()): { fromDate: string; toDate: string } => {
  const toDate = new Date(referenceDate);
  const fromDate = new Date(toDate);
  fromDate.setDate(fromDate.getDate() - daysBack);

  return {
    fromDate: fromDate.toISOString().split('T')[0],
    toDate: toDate.toISOString().split('T')[0]
  };
};

/**
 * Format number with commas for better readability
 * @param num - Number to format
 * @returns Formatted string
 */
export const formatNumber = (num: number): string => {
  return num.toLocaleString('en-IN');
};

/**
 * Truncate string to specified length
 * @param str - String to truncate
 * @param maxLength - Maximum length
 * @returns Truncated string with ellipsis if needed
 */
export const truncateString = (str: string, maxLength: number): string => {
  if (str.length <= maxLength) return str;
  return str.substring(0, maxLength - 3) + '...';
};
